// One limit row in the Spending limits section: read-only cells
// with usage bars, the inline editor, and the inline one-time extra form.
// Budget-group membership lives in SpendGroupMembers.tsx.

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SpendCell, SpendRow } from "@/lib/api";
import {
  checkDraft,
  effectiveCap,
  fmtCap,
  fmtSpent,
  hasCap,
  parseExtra,
  warnHint,
  PERIOD_WORD,
  toneOf,
  type Draft,
  type DraftContext,
  type DraftResult,
  type Hint,
  type Period,
  type Tone,
} from "@/lib/spend-limits";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

/** Name · Daily · Monthly · Warn at · actions; stacks below `md`. */
export const ROW_GRID =
  "grid grid-cols-2 gap-x-5 gap-y-2 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_4.5rem_10.5rem] md:gap-y-1.5";

/** Top padding that lines a column up with the inputs under the field labels (label h-5 + gap-1). */
export const UNDER_LABEL = "md:pt-6";

/** A disabled Save or Add stays legible on the amber edit row. */
export const SUBMIT_DISABLED = "disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100";

const TONE_TEXT: Record<Tone, string> = {
  "": "",
  warn: "font-medium text-amber-700 dark:text-amber-400",
  crit: "font-medium text-destructive",
};
const TONE_FILL: Record<Tone, string> = {
  "": "bg-primary",
  warn: "bg-amber-500",
  crit: "bg-destructive",
};

/** Who hears about a limit at its warning level (boo-backend docs/spend-limits.md §6.2, §6.4). */
const WARN_AT_HINT =
  "You get a heads-up at this level: in Boo for your own limit, admins for shared limits, plus an email to the owner and billing admins for the organization limit.";

export const LINK_BUTTON = "text-primary underline underline-offset-4 hover:no-underline disabled:opacity-50";

function MobileLabel({ children }: { children: ReactNode }) {
  return <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:hidden">{children}</span>;
}

/** A field label over an edit control, the same height on every column so the controls line up. */
export function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="h-5 truncate text-xs leading-5 font-medium text-muted-foreground">
      {children}
    </label>
  );
}

function UsageCell({
  label,
  cell,
  warnPercent,
  perPerson,
  period,
  shared,
  onRemoveExtra,
  removing,
}: {
  label: string;
  cell: SpendCell;
  warnPercent: number;
  /** The default row shows the cap each person gets, not usage. */
  perPerson?: boolean;
  /** A budget group's amounts are one pot for all its people. */
  shared?: boolean;
  period: Period;
  onRemoveExtra?: () => void;
  removing?: boolean;
}) {
  const cap = effectiveCap(cell);
  if (cap == null) {
    return (
      <div className="flex min-w-0 flex-col gap-1 text-sm">
        <MobileLabel>{label}</MobileLabel>
        <span className="text-muted-foreground">No limit</span>
      </div>
    );
  }
  if (perPerson) {
    return (
      <div className="flex min-w-0 flex-col gap-1 text-sm">
        <MobileLabel>{label}</MobileLabel>
        <span className="tabular-nums">{fmtCap(cap)} each</span>
      </div>
    );
  }
  const pct = Math.min(100, cap > 0 ? (cell.spentUsd / cap) * 100 : 100);
  const tone = toneOf(cell.spentUsd, cap, warnPercent);
  return (
    <div className="flex min-w-0 flex-col gap-1.5 text-sm">
      <MobileLabel>{label}</MobileLabel>
      <span className={cn("tabular-nums", TONE_TEXT[tone])}>
        {fmtSpent(cell.spentUsd)} of {fmtCap(cap)}
        {shared && " shared"}
      </span>
      <div
        className="relative h-1.5 rounded-full bg-muted ring-1 ring-inset ring-border"
        role="img"
        aria-label={`${Math.round(pct)}% used, warning at ${warnPercent}%`}
      >
        <div className={cn("absolute inset-y-0 left-0 rounded-full", TONE_FILL[tone])} style={{ width: `${pct}%` }} />
        <span
          className="absolute -top-[3px] -bottom-[3px] w-[1.5px] bg-muted-foreground/60"
          style={{ left: `${Math.min(warnPercent, 100)}%` }}
        />
      </div>
      {cell.extraUsd > 0 && (
        <span className="text-xs tabular-nums text-muted-foreground">
          Raised by {fmtCap(cell.extraUsd)} for {PERIOD_WORD[period]}
          {onRemoveExtra && (
            <>
              {" · "}
              <button type="button" className={LINK_BUTTON} disabled={removing} onClick={onRemoveExtra}>
                Undo
              </button>
            </>
          )}
        </span>
      )}
    </div>
  );
}

/** The shadcn Input with a "$" prefix, or a "%" suffix for the warn level. */
export function MoneyInput({
  id,
  label,
  value,
  bad,
  describedBy,
  autoFocus,
  placeholder,
  suffix,
  className,
  onChange,
}: {
  id: string;
  /** Accessible name when no visible label points at the field. */
  label?: string;
  value: string;
  bad?: boolean;
  /** Id of the hint or error text under the field. */
  describedBy?: string;
  autoFocus?: boolean;
  placeholder?: string;
  suffix?: boolean;
  className?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className={cn("relative", className)}>
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground",
          suffix ? "right-2.5" : "left-2.5",
        )}
      >
        {suffix ? "%" : "$"}
      </span>
      <Input
        id={id}
        inputMode={suffix ? "numeric" : "decimal"}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={bad || undefined}
        aria-describedby={describedBy}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className={cn("bg-background tabular-nums", suffix ? "pr-6" : "pl-6")}
      />
    </div>
  );
}

export function Hints({ id, hints }: { id?: string; hints: Hint[] }) {
  if (!hints.length) return null;
  return (
    <div id={id} className="col-span-full flex flex-col gap-0.5 text-[13px] text-pretty">
      {hints.map(([kind, text]) => (
        <span
          key={text}
          className={cn(
            kind === "err" && "text-destructive",
            kind === "impact" && "text-amber-700 dark:text-amber-400",
            kind === "info" && "text-muted-foreground",
          )}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

/** The three editable fields, shared by the row editor and the add rows. */
export function DraftFields({
  idPrefix,
  draft,
  result,
  onChange,
  used,
  describedBy,
  autoFocus,
}: {
  idPrefix: string;
  draft: Draft;
  result: DraftResult;
  onChange: (d: Draft) => void;
  used?: { daily: number; monthly: number };
  describedBy?: string;
  /** Focus the daily field on mount (when it is the row's first field). */
  autoFocus?: boolean;
}) {
  return (
    <>
      <div className="flex min-w-0 flex-col gap-1">
        <FieldLabel htmlFor={`${idPrefix}-daily`}>Daily limit</FieldLabel>
        <MoneyInput
          id={`${idPrefix}-daily`}
          value={draft.daily}
          bad={result.dailyBad}
          describedBy={describedBy}
          autoFocus={autoFocus}
          placeholder="No limit"
          onChange={(daily) => onChange({ ...draft, daily })}
        />
        {used && <span className="text-xs tabular-nums text-muted-foreground">{fmtSpent(used.daily)} used today</span>}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <FieldLabel htmlFor={`${idPrefix}-monthly`}>Monthly limit</FieldLabel>
        <MoneyInput
          id={`${idPrefix}-monthly`}
          value={draft.monthly}
          bad={result.monthlyBad}
          describedBy={describedBy}
          placeholder="No limit"
          onChange={(monthly) => onChange({ ...draft, monthly })}
        />
        {used && <span className="text-xs tabular-nums text-muted-foreground">{fmtSpent(used.monthly)} used this month</span>}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <FieldLabel htmlFor={`${idPrefix}-warn`}>Warn at</FieldLabel>
        <MoneyInput
          id={`${idPrefix}-warn`}
          value={draft.warn}
          bad={result.warnBad}
          describedBy={describedBy}
          suffix
          onChange={(warn) => onChange({ ...draft, warn })}
        />
      </div>
    </>
  );
}

export type RowView = {
  key: string;
  row: SpendRow;
  name: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  isDefault?: boolean;
  /** Whether a one-time extra can be added (not on the default row). */
  extraable?: boolean;
  /** Budget group: amounts read "… shared". */
  shared?: boolean;
  ctx: DraftContext;
  /** Extra guidance while editing ("Replaces the default for Sam…"). */
  editHints?: Hint[];
};

/** Inline "Raise for today": a one-time amount for today or this month, for the periods the row caps. */
function ExtraForm({
  view,
  saving,
  error,
  onAdd,
  onCancel,
}: {
  view: RowView;
  saving: boolean;
  error: string | null;
  onAdd: (period: Period, amountUsd: number) => void;
  onCancel: () => void;
}) {
  const periods = (["daily", "monthly"] as const).filter((p) => view.row[p].capUsd != null);
  const [period, setPeriod] = useState<Period>(periods[0] ?? "daily");
  const [amount, setAmount] = useState("");
  const value = parseExtra(amount);
  const bad = amount.trim() !== "" && value == null;
  const submit = () => value != null && !saving && onAdd(period, value);
  return (
    <div
      className="flex flex-wrap items-center gap-2 bg-muted/40 px-4 py-2.5 text-sm"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") submit();
      }}
    >
      <label htmlFor={`extra-${view.key}`} className="text-muted-foreground">
        Raise the limit by
      </label>
      <MoneyInput
        id={`extra-${view.key}`}
        value={amount}
        bad={bad}
        describedBy={bad || error ? `extra-${view.key}-error` : undefined}
        autoFocus
        className="w-28"
        onChange={setAmount}
      />
      <span className="text-muted-foreground">for</span>
      {periods.length > 1 ? (
        <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <SelectTrigger size="sm" className="w-32" aria-label="Raise for">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {periods.map((p) => (
              <SelectItem key={p} value={p}>
                {PERIOD_WORD[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span>{PERIOD_WORD[period]}</span>
      )}
      <Button size="sm" disabled={value == null || saving} onClick={submit}>
        {saving ? "Raising…" : "Raise"}
      </Button>
      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
      {(bad || error) && <span id={`extra-${view.key}-error`} className="basis-full text-[13px] text-destructive">{error ?? "Enter an amount above $0."}</span>}
    </div>
  );
}

export function LimitRow({
  view,
  canEdit,
  editing,
  saved,
  draft,
  onDraft,
  onEdit,
  onCancel,
  onSave,
  saving,
  error,
  nameEditor,
  extraHints,
  extra,
}: {
  view: RowView;
  canEdit: boolean;
  editing: boolean;
  saved: boolean;
  draft: Draft | null;
  onDraft: (d: Draft) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (r: DraftResult) => void;
  saving: boolean;
  error: string | null;
  /** Replaces the name column while editing (group rename). */
  nameEditor?: ReactNode;
  /** Extra lines under the hints while editing ("Use the default instead"). */
  extraHints?: ReactNode;
  /** One-time extra controls for this row. */
  extra: {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
    onAdd: (period: Period, amountUsd: number) => void;
    onRemove: (period: Period) => void;
    busy: boolean;
    error: string | null;
    removeError: string | null;
  };
}) {
  const { row } = view;
  const nameCol = (
    <div className="col-span-2 flex min-w-0 flex-col gap-0.5 md:col-span-1">
      <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
        {view.icon}
        <span className="truncate">{view.name}</span>
      </span>
      {view.sub && <span className="text-[12.5px] text-muted-foreground">{view.sub}</span>}
    </div>
  );

  if (editing && draft) {
    const result = checkDraft(draft, view.ctx);
    const warn = warnHint(draft, result);
    const hints: Hint[] = [
      ...(error ? [["err", error] as Hint] : []),
      ...result.hints,
      ...(view.editHints ?? []),
      ...(warn ? [warn] : []),
    ];
    const hintsId = `sl-${view.key}-hints`;
    return (
      <div
        className={cn(ROW_GRID, "items-start bg-amber-50/70 px-4 py-3 dark:bg-amber-400/[0.06]")}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT" && result.ok && !saving) onSave(result);
        }}
      >
        {nameEditor ?? (
          <div className={cn("col-span-2 flex min-w-0 flex-col md:col-span-1", UNDER_LABEL)}>
            <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium md:h-8">
              {view.icon}
              <span className="truncate">{view.name}</span>
            </span>
            {view.sub && <span className="text-[12.5px] text-muted-foreground">{view.sub}</span>}
          </div>
        )}
        <DraftFields
          idPrefix={`sl-${view.key}`}
          draft={draft}
          result={result}
          onChange={onDraft}
          describedBy={hints.length ? hintsId : undefined}
          autoFocus={!nameEditor}
          used={view.isDefault ? undefined : { daily: row.daily.spentUsd, monthly: row.monthly.spentUsd }}
        />
        <div className={cn("col-span-2 flex justify-end gap-1.5 md:col-span-1", UNDER_LABEL)}>
          <Button className={SUBMIT_DISABLED} disabled={!result.ok || saving} onClick={() => onSave(result)}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button variant="ghost" className="text-muted-foreground" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
        <Hints id={hintsId} hints={hints} />
        {extraHints}
      </div>
    );
  }

  const canExtra = canEdit && view.extraable && (row.daily.capUsd != null || row.monthly.capUsd != null);
  const removeFor = (p: Period) => (canEdit ? () => extra.onRemove(p) : undefined);
  return (
    <>
      <div className={cn(ROW_GRID, "items-start px-4 py-3")}>
        {nameCol}
        <UsageCell label="Daily" cell={row.daily} warnPercent={row.warnPercent} perPerson={view.isDefault} period="daily" shared={view.shared} onRemoveExtra={removeFor("daily")} removing={extra.busy} />
        <UsageCell label="Monthly" cell={row.monthly} warnPercent={row.warnPercent} perPerson={view.isDefault} period="monthly" shared={view.shared} onRemoveExtra={removeFor("monthly")} removing={extra.busy} />
        <div className="flex flex-col gap-1 text-sm tabular-nums">
          <MobileLabel>Warn at</MobileLabel>
          {hasCap(row) ? (
            `${row.warnPercent}%`
          ) : (
            <>
              <span className="text-muted-foreground" aria-hidden>
                —
              </span>
              <span className="sr-only">No warning without a limit</span>
            </>
          )}
        </div>
        <div className="col-span-2 flex items-center justify-end gap-1 md:col-span-1">
          {saved && <span className="mr-1 text-[12.5px] font-medium text-emerald-600 dark:text-emerald-400">Saved</span>}
          {canExtra && !extra.open && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={extra.onOpen}>
              {row.daily.capUsd != null ? "Raise for today" : "Raise for this month"}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onEdit} aria-label={`Edit ${typeof view.name === "string" ? view.name : "limit"}`}>
              Edit
            </Button>
          )}
        </div>
      </div>
      {extra.removeError && <div className="px-4 pb-2.5 text-[13px] text-destructive">{extra.removeError}</div>}
      {canExtra && extra.open && (
        <ExtraForm view={view} saving={extra.busy} error={extra.error} onAdd={extra.onAdd} onCancel={extra.onClose} />
      )}
    </>
  );
}

/**
 * A sub-section's title row, which also names the columns on wide screens so
 * the headings sit next to the rows they label. Without a title it is only the
 * column names, hidden below `md` where each cell carries its own label.
 */
export function SectionHead({ children }: { children?: ReactNode }) {
  return (
    <div
      className={cn(ROW_GRID, "border-b bg-muted/40 px-4 py-1.5 text-xs font-medium text-muted-foreground", !children && "hidden md:grid")}
    >
      <span className="col-span-2 md:col-span-1">{children}</span>
      <span className="hidden md:block">Daily</span>
      <span className="hidden md:block">Monthly</span>
      <span className="hidden md:block">
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="cursor-help underline decoration-dotted underline-offset-4">
              Warn at
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72 text-pretty">{WARN_AT_HINT}</TooltipContent>
        </Tooltip>
      </span>
      <span className="hidden md:block" />
    </div>
  );
}
